import { useContext, useState } from "react";
import { useRevealSecret, useRevealSecretMember } from "services/secrets";
import {
  RevealSecretRequest,
  RevealSecretResponse,
} from "protogen/secrets_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdvisor } from "common/userUtils";
import { EphemeralSecret } from "./types";
import { attachmentsToUploads } from "../creation/FileUploader";
import { Family } from "protogen/advisors_service_pb";
import ReactiveDialog from "../common/ReactiveDialog";
import { Box, Button, Typography } from "@mui/material";
import { LockKeyhole } from "lucide-react";
import { getTypeView } from "./SecretView";
import SnackPack, { SnackbarMessage } from "../common/SnackPack";

interface SecretRevealConfirmationProps {
  open: boolean;
  onClose: () => void;
  secretRef: string;
  family?: Family;
}

export const SecretRevealConfirmation = ({
  open,
  onClose,
  secretRef,
  family,
}: SecretRevealConfirmationProps) => {
  const [secret, setSecret] = useState<EphemeralSecret | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = isUserAdvisor(currentUser);

  const onReveal = (response: RevealSecretResponse) => {
    const ephemeralSecret: EphemeralSecret = {
      ref: response?.secret?.ref,
      name: response?.secret?.name || "",
      valueType: response?.secret?.valueType || "",
      value: response.secretValue,
      attachments: response?.attachments || [],
      uploads: attachmentsToUploads(response?.attachments || []),
    };
    setSecret(ephemeralSecret);
    setShowConfirmation(false);
    setSnackPack((prev) => [
      ...prev,
      {
        message: "Access Recorded",
        key: new Date().getTime(),
      },
    ]);
  };

  const { request: revealAdvisorSecret, loading: revealingAdvisor } =
    useRevealSecret(onReveal);
  const { request: revealMemberSecret, loading: revealingMember } =
    useRevealSecretMember(onReveal);
  const revealSecretRequest = isAdvisor
    ? revealAdvisorSecret
    : revealMemberSecret;
  const loading = isAdvisor ? revealingAdvisor : revealingMember;

  const handleClose = () => {
    setSecret(null);
    setShowConfirmation(true);
    onClose();
  };

  const handleConfirm = () => {
    revealSecretRequest(new RevealSecretRequest({ secretRef }));
  };

  const secretType = secret ? getTypeView(secret) : null;
  const secretLabel = secretType?.label.toLowerCase() || "secret";

  return (
    <ReactiveDialog fullWidthSize="sm" open={open} onClose={handleClose}>
      {showConfirmation ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "24px",
            gap: "16px",
          }}
        >
          <Typography variant="title">Reveal Secret</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px",
              borderRadius: "12px",
              backgroundColor: "#FAF9FA",
            }}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                padding: "8px",
                border: `1px solid ${theme.palette.border}`,
                borderRadius: "50px",
              })}
            >
              <LockKeyhole size={20} stroke="#8E9598" />
            </Box>
            <Typography variant="bodySmall" color="text.secondary">
              This information is encrypted and requires additional verification
              to view.
            </Typography>
          </Box>
          <Typography variant="body" color="text.secondary">
            Are you sure you want to reveal this secret?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
          >
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={loading}
            >
              Reveal Secret
            </Button>
          </Box>
        </Box>
      ) : (
        secret &&
        secretType && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "24px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "24px",
              }}
            >
              <Typography variant="title">
                {secretLabel.charAt(0).toUpperCase() + secretLabel.slice(1)}
              </Typography>
            </Box>
            <Box
              sx={{
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px",
                borderRadius: "12px",
                backgroundColor: "#FAF9FA",
              }}
            >
              <Box
                sx={(theme) => ({
                  display: "flex",
                  padding: "8px",
                  border: `1px solid ${theme.palette.border}`,
                  borderRadius: "50px",
                })}
              >
                <LockKeyhole size={20} stroke="#8E9598" />
              </Box>
              <Typography variant="bodySmall" color="text.secondary">
                This information has additional safeguards and is encrypted at
                rest for security.
              </Typography>
            </Box>
            <Box sx={{ marginBottom: "24px" }}>
              <secretType.view
                secret={secret}
                updateFormData={() => {}}
                errors={null}
                family={family}
                onUploadChange={() => {}}
                isEdit={true}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={handleClose}>Close</Button>
            </Box>
          </Box>
        )
      )}
      <SnackPack
        snackPack={snackPack}
        alertSeverity="warning"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </ReactiveDialog>
  );
};
